import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import type { Config } from 'atoms/Rtool/newConfigAtoms';
import type { Powertrain } from 'atoms/powertrainAtom';
import {
    copyJobsAtom, buildDestinationKey, restoreLatestCopyJobAtom, copyJobTerminalEventsAtom,
    type CopyJobEntry,
} from 'atoms/Rtool/copyJobAtoms';
import { isConfigComplete } from 'utils/utils';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import type { SnackbarAction } from 'atoms/snackbarAtom';
import { classifySummary, buildResultMessage } from 'components/FailedItemsList';
import { buildFailedJobMessage } from 'utils/copyJobProgress';

// コピー先が確定した時点で、未通知の進行中/完了ジョブを復元する（issue #43 AC6）。
// copyToConfigAtom / stageConfigAtom は互いに独立した選択のため、呼び出しは宛先スコープごとに
// 1箇所（DataRegistration/ExistingProjectsの各トップレベル）に集約する。
export const useRestoreCopyJobOnMount = (powertrain: Powertrain, config: Config): void => {
    const restore = useSetAtom(restoreLatestCopyJobAtom);
    const destinationKey = isConfigComplete(config) ? buildDestinationKey(powertrain, config) : null;

    useEffect(() => {
        if (!destinationKey) return;
        restore({ powertrain, config });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationKey]);
};

// 指定コピー先（powertrain+6ID）に対応する監視エントリ1件を読む（issue #44）。
// config未確定時はdestinationKeyを作れないためundefinedを返す。
export const useCopyJobEntry = (powertrain: Powertrain, config: Config): CopyJobEntry | undefined => {
    const entries = useAtomValue(copyJobsAtom);
    if (!isConfigComplete(config)) return undefined;
    return entries[buildDestinationKey(powertrain, config)];
};

// このコピー先への書き込み操作を抑止すべきか（issue #43 §2.5）。
// watchState==='watching'だけでなく'error'（401連続失敗等でクライアント側の監視を諦めた状態）も
// 抑止対象に含める。'error'はサーバ側のコピーが終わったことを意味しない（クライアントが
// 状態取得を諦めただけ）ため、'terminal'（completed/failed）に到達するまでは塞ぎ続ける
// （/code-review xhigh所見: errorで抑止が解除されると実行中のコピー先に書き込めてしまう）。
export const useIsCopyJobBlocking = (powertrain: Powertrain, config: Config): boolean => {
    const entry = useCopyJobEntry(powertrain, config);
    return entry !== undefined && entry.watchState !== 'terminal';
};

// 終端(completed/failed)に達していない全ジョブ（複数コピー先で同時実行中のものを含む）。
// アプリ全体ヘッダーの常設インジケーター・document.titleマーク（issue #44 §2.3/§2.4）で使う。
export const useActiveCopyJobEntries = (): CopyJobEntry[] => {
    const entries = useAtomValue(copyJobsAtom);
    return Object.values(entries).filter((entry) => entry.watchState !== 'terminal');
};

// 別タブ・別画面にいる間も進行中コピーの有無がわかるよう、document.titleに件数を反映する
// （issue #44 §2.4）。マウント時のtitleを基準として保持し、0件に戻ったら復元する。
// watchState==='error'（監視を諦めた状態）は「実行中」ではないため[コピー中]とは区別する
// （/code-review所見: errorのまま恒久的に[コピー中]と表示され続け、ユーザーが停止に気づけない）。
export const useCopyJobTitleIndicator = (): void => {
    const activeEntries = useActiveCopyJobEntries();
    const runningCount = activeEntries.filter((entry) => entry.watchState === 'watching').length;
    const hasError = activeEntries.some((entry) => entry.watchState === 'error');
    const baseTitleRef = useRef<string | null>(null);

    useEffect(() => {
        if (baseTitleRef.current === null) {
            baseTitleRef.current = document.title;
        }
        const baseTitle = baseTitleRef.current;
        const mark = runningCount > 0 ? '[コピー中]' : hasError ? '[コピー要確認]' : null;
        document.title = mark ? `${mark} ${baseTitle}` : baseTitle;
        return () => {
            document.title = baseTitle;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runningCount, hasError]);
};

// 別画面にいるときの完了通知（issue #45 §4経路2）。#43のcode-review対応で追加された最低限の
// 通知に、結果詳細への導線（「結果を見る」action。issue §4「結果詳細へのリンク」要件）と、
// failed用の正式な文言（buildFailedJobMessage。issue #45 §3の要件どおりmessageを参照しない）を追加する。
// 呼び出しは画面遷移に依存しない場所（MainLayout等）で1回だけ行う。
// onViewResult: 「結果を見る」action押下時のコピー先画面への遷移処理。react-router-domの
// useNavigateはこのファイルに持たせず呼び出し元（MainPage/index.tsx）に委ねる
// （このhookをimportするだけでreact-router-domの解決（v7、CRA/jest 27非対応）が必要になり、
// react-router-domを一切importしない既存の呼び出し元コンポーネントのテスト
// ExcelImport.test.tsx/ManualImport.test.tsx/ExistingProjects/index.test.tsx/ProjectCopy.test.tsx
// が軒並み壊れるため）。
export const useNotifyCopyJobTerminalEvents = (
    onViewResult?: (entry: CopyJobEntry) => void,
): void => {
    const [events, setEvents] = useAtom(copyJobTerminalEventsAtom);
    const jobEntries = useAtomValue(copyJobsAtom);
    const { showSuccess, showError } = useSnackbar();

    useEffect(() => {
        if (events.length === 0) return;
        events.forEach(({ destinationKey, snapshot }) => {
            // イベントを push した書き込み（copyJobAtoms.ts）は必ず先に同じ destinationKey へ
            // entry を書いているため、通常はここで見つかる。無い場合（理論上ほぼ起こらない）は
            // 遷移先が分からないため action 無しで通知のみ行う。
            const entry = jobEntries[destinationKey];
            const action: SnackbarAction | undefined = entry && onViewResult
                ? { label: '結果を見る', onClick: () => onViewResult(entry) }
                : undefined;

            // 終端イベントは destinationKey ごとに一意な発生であり、複数件が同じ結果文言に
            // なることもある（例: 同じ件数で成功）。showSuccess/showError の連続 dedup や
            // MAX_SNACKBARS 上限切り捨てに巻き込まれて後続イベントの通知が消えないよう、
            // guaranteed: true で両方をバイパスする（issue #47 要件1「必ず1回通知される」）。
            if (snapshot.status === 'completed') {
                const resultMessage = buildResultMessage(snapshot.summary);
                if (classifySummary(snapshot.summary) === 'all_success') {
                    showSuccess(`計算セット丸ごとコピーが完了しました。${resultMessage}`, action, { guaranteed: true });
                } else {
                    showError(`計算セット丸ごとコピーが完了しましたが、一部失敗しています。${resultMessage}`, action, { guaranteed: true });
                }
                return;
            }
            showError(buildFailedJobMessage(snapshot), action, { guaranteed: true });
        });
        // push側（copyJobAtoms.ts）は常に await の継続として非同期にイベントを積むため、
        // 本番での実際の競合窓は「レンダがこの events クロージャを確定した後〜このeffectの
        // 実行（flush）まで」の間であり、forEach実行そのものに同期的に割り込むわけではない。
        // setEvents([]) のように無条件で空にすると、この窓で積まれた別destinationKeyの
        // イベントを一度も通知せずに消してしまう（issue #47）。このeffectが実際に処理した
        // idだけをprev（setEvents時点の最新値）から取り除くことで、その割り込みイベントは
        // 消費されずに残り、events依存の再実行で次に通知される。
        const consumedIds = new Set(events.map(({ id }) => id));
        setEvents((prev) => prev.filter((event) => !consumedIds.has(event.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);
};
